import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from '@/lib/analytics';
import {
  ArrowRight,
  CheckCircle2,
  GraduationCap,
  ShieldCheck,
  Mail,
  Network,
  Layers,
  Calendar,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const WHY_ORACLE_EDUCATION = [
  {
    icon: Layers,
    title: 'Fund accounting & encumbrances',
    body: 'Districts and institutions account by fund, not corporate GL. EZ Cloud codes and batches invoices per fund and respects encumbrance controls in Oracle EBS.',
  },
  {
    icon: GraduationCap,
    title: 'Grant tracking & restricted dollars',
    body: 'Grant- and award-funded spend needs clean attribution. EZ Cloud reads your Oracle project/award structures and routes invoices for the right coding and approval.',
  },
  {
    icon: Network,
    title: 'Decentralized purchasing across schools',
    body: 'Invoices arrive against dozens of schools, departments, and cost centres. EZ Cloud routes each to the right approver from your EBS hierarchy automatically.',
  },
  {
    icon: Calendar,
    title: 'Seasonal peaks & district controls',
    body: 'School-year budget cycles, closed-period and prior-year rules, approval thresholds, attestation, and district-specific tax — handled in the workflow.',
  },
];

const HOW_IT_WORKS = [
  {
    title: 'Native Oracle EBS posting',
    body: 'EZ Cloud writes to the standard Payables Open Interface and runs Payables Open Interface Import — no custom EBS schema, no base-table writes.',
  },
  {
    title: 'Deployed in your environment',
    body: 'An integration server runs in the district or institution environment, connecting securely to Oracle E-Business Suite — keeping data inside your controls.',
  },
  {
    title: 'Fund, tax, and period controls',
    body: 'Per-fund coding and batching, district tax handling (local/state/transit/prepared-food), closed-period and prior-year validation, approval thresholds and attestation.',
  },
  {
    title: 'AI email exception prevention',
    body: "EZ Cloud's AI reads, classifies, and routes inbound supplier email before invoices reach the workflow — the manual triage AP teams do by hand today.",
  },
];

const FAQS = [
  {
    q: 'Does EZ Cloud work with Oracle E-Business Suite for K-12 and higher education?',
    a: 'Yes. EZ Cloud integrates with Oracle EBS (R12 / 12.2) by writing to the standard Payables Open Interface and running Payables Open Interface Import — no custom schema. It is live in production at a large US public school district running Oracle EBS.',
  },
  {
    q: 'Does it support fund accounting, encumbrances, and grant tracking?',
    a: 'Yes. EZ Cloud codes and batches invoices per fund, respects Oracle EBS encumbrance controls, and reads your project/award structures so grant- and restricted-fund spend is attributed and approved correctly.',
  },
  {
    q: 'How is it deployed — cloud or on-premises?',
    a: 'EZ Cloud deploys an integration server in your own environment that connects securely to Oracle EBS, so finance data stays inside your controls. Capture, validation, approval, and AI email prevention run in the EZ Cloud platform.',
  },
  {
    q: 'How long does implementation take?',
    a: 'Typical timeline is 8–12 weeks depending on environment and integration complexity. Critical-path items are supplier-master cleanup, fund/approval mapping, network access to EBS, and sandbox testing with a representative invoice sample.',
  },
];

const EducationOracleEBS = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle EBS AP & Invoice Automation for K-12 & Education | EZ Cloud"
        description="Invoice automation and AP automation for K-12 and higher education on Oracle E-Business Suite. AI invoice data extraction, fund accounting, encumbrances, grant tracking, district tax, and AI email exception prevention — invoices posted natively via the Payables Open Interface."
        canonical="https://ezcloud.ai/solutions/education-oracle-ebs"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/education-oracle-ebs' },
          { name: 'Education on Oracle EBS', url: 'https://ezcloud.ai/solutions/education-oracle-ebs' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <GraduationCap className="w-4 h-4" aria-hidden />
              Solutions · Education × Oracle EBS
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              AP automation for K-12 &amp; education on Oracle E-Business Suite
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Districts and institutions run AP on fund accounting, grants, and public-sector controls.
              EZ Cloud automates it end-to-end on Oracle EBS — posting natively through the Payables
              Open Interface, no custom schema.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-white/10 border border-white/20 text-sm text-white/90">
              <ShieldCheck className="w-4 h-4 text-accent" aria-hidden />
              <span>
                Published on the{' '}
                <a
                  href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline decoration-accent/50 underline-offset-2 hover:decoration-accent"
                >
                  Oracle Reference Architecture for OCI
                </a>
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" variant="accent" className="font-semibold" onClick={() => openCalendlyDemo()}>
                Book an Oracle AP demo
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/customers/school-district-oracle-ebs">Read the school-district story</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Manual-before framing */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              From manual to automated
            </p>
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              Most districts and institutions still run AP manually — keying invoices across dozens of
              schools and funds. EZ Cloud delivers end-to-end invoice automation on Oracle EBS, and
              per Oracle's own technical case study helps companies capture, authorize, and pay
              invoices{' '}
              <a
                href="https://www.oracle.com/cloud/technical-case-studies/ez-cloud/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
              >
                up to 80% faster than on-premises AP
              </a>
              .
            </p>
          </div>
        </section>

        {/* Why Oracle education AP is different */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Why education AP on Oracle is different
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Public-sector and institutional finance run on fund accounting and grants, not corporate
              GL. EZ Cloud was built for it.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHY_ORACLE_EDUCATION.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              How EZ Cloud runs on Oracle EBS
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {HOW_IT_WORKS.map((item) => (
                <Card key={item.title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Proof */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-3">
                <GraduationCap className="w-4 h-4" aria-hidden />
                Proof point
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                A large US public school district, live on Oracle EBS
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                A large US K-12 district replaced manual AP with EZ Cloud on Oracle E-Business Suite —
                live in production across ~25 schools and departments, with fund accounting, district
                tax handling, and in-workflow receiving, all posting natively into Oracle.
              </p>
              <Link
                to="/customers/school-district-oracle-ebs"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Read the full story
                <ArrowRight className="w-4 h-4 ml-1.5" aria-hidden />
              </Link>
            </Card>

            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-3">
                <Layers className="w-5 h-5 text-primary flex-shrink-0" aria-hidden />
                Fund accounting &amp; encumbrances
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-3">
                <Network className="w-5 h-5 text-primary flex-shrink-0" aria-hidden />
                Native EBS, no custom schema
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-3">
                <Mail className="w-5 h-5 text-primary flex-shrink-0" aria-hidden />
                AI email exception prevention
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              See AP automation on your Oracle EBS
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Book a 30-minute walkthrough — fund accounting, district tax, and in-workflow receiving,
              against your invoice sample.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" variant="accent" className="font-semibold" onClick={() => openCalendlyDemo()}>
                Book an Oracle AP demo
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/industries/education">Explore education AP</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default EducationOracleEBS;
